import { useMemo } from 'react';
import ReactMarkdown from 'react-markdown';

interface MarkdownDescriptionProps {
  content: string;
  className?: string;
}

type ParsedMarkdownContent = {
  body: string;
  metadataEntries: Array<{ key: string; value: string }>;
  hasFrontmatter: boolean;
  hasValidMetadata: boolean;
};

const FRONTMATTER_PATTERN = /^---\s*\r?\n([\s\S]*?)\r?\n---\s*(?:\r?\n|$)/;

function parseMarkdownContent(content: string): ParsedMarkdownContent {
  const frontmatterMatch = content.match(FRONTMATTER_PATTERN);

  if (!frontmatterMatch) {
    return {
      body: content,
      metadataEntries: [],
      hasFrontmatter: false,
      hasValidMetadata: false,
    };
  }

  const metadataSource = frontmatterMatch[1] || '';
  const body = content.slice(frontmatterMatch[0].length);
  const metadataEntries: Array<{ key: string; value: string }> = [];
  let invalidLineFound = false;

  metadataSource.split(/\r?\n/).forEach((line) => {
    const trimmed = line.trim();

    if (!trimmed || trimmed.startsWith('#')) {
      return;
    }

    const separatorIndex = trimmed.indexOf(':');
    if (separatorIndex <= 0) {
      invalidLineFound = true;
      return;
    }

    const key = trimmed.slice(0, separatorIndex).trim();
    const value = trimmed.slice(separatorIndex + 1).trim();

    if (!key || !value) {
      invalidLineFound = true;
      return;
    }

    metadataEntries.push({ key, value });
  });

  return {
    body: body || '',
    metadataEntries,
    hasFrontmatter: true,
    hasValidMetadata: !invalidLineFound && metadataEntries.length > 0,
  };
}

export function MarkdownDescription({ content, className = '' }: MarkdownDescriptionProps) {
  const parsedContent = useMemo(() => parseMarkdownContent(content), [content]);
  const shouldFallbackToPlainText =
    parsedContent.hasFrontmatter && !parsedContent.hasValidMetadata;

  if (shouldFallbackToPlainText) {
    return (
      <p className={`whitespace-pre-wrap text-muted-foreground ${className}`}>
        {content}
      </p>
    );
  }

  const markdownBody = parsedContent.hasValidMetadata
    ? parsedContent.body
    : content;

  return (
    <div className={`prose prose-sm max-w-none ${className}`}>
      {parsedContent.hasValidMetadata && (
        <div className="mb-4 rounded-lg border border-border bg-muted/40 p-4 not-prose">
          <h4 className="mb-3 text-sm font-semibold text-muted-foreground">Metadata</h4>
          <dl className="grid grid-cols-1 gap-2 sm:grid-cols-2">
            {parsedContent.metadataEntries.map((entry) => (
              <div key={entry.key} className="rounded-md bg-white px-3 py-2 border border-border">
                <dt className="text-xs uppercase tracking-wide text-muted-foreground">{entry.key}</dt>
                <dd className="text-sm text-muted-foreground break-words">{entry.value}</dd>
              </div>
            ))}
          </dl>
        </div>
      )}

      <ReactMarkdown
        components={{
          // Customize rendering if needed
          h1: ({ children }) => <h1 className="text-2xl font-bold mb-4 mt-6">{children}</h1>,
          h2: ({ children }) => <h2 className="text-xl font-bold mb-3 mt-5">{children}</h2>,
          h3: ({ children }) => <h3 className="text-lg font-semibold mb-2 mt-4">{children}</h3>,
          p: ({ children }) => <p className="mb-3 leading-relaxed">{children}</p>,
          ul: ({ children }) => <ul className="list-disc pl-6 mb-3 space-y-1">{children}</ul>,
          ol: ({ children }) => <ol className="list-decimal pl-6 mb-3 space-y-1">{children}</ol>,
          img: ({ src, alt }) => {
            if (!src) return null;

            return (
              <a
                href={src}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex"
              >
                <img
                  src={src}
                  alt={alt || 'Image'}
                  className="h-24 w-24 rounded-lg border border-border object-cover shadow-sm transition-opacity hover:opacity-90"
                  loading="lazy"
                />
              </a>
            );
          },
          code: ({ children, className }) => {
            const isInline = !className;
            return isInline ? (
              <code className="bg-muted/40 px-1.5 py-0.5 rounded text-sm font-mono text-chart-4">
                {children}
              </code>
            ) : (
              <code className="block bg-muted/40 text-muted-foreground p-4 rounded-lg overflow-x-auto font-mono text-sm">
                {children}
              </code>
            );
          },
          blockquote: ({ children }) => (
            <blockquote className="border-l-4 border-primary/40 pl-4 py-2 my-4 bg-primary/10 italic">
              {children}
            </blockquote>
          ),
          a: ({ children, href }) => (
            <a href={href} className="text-primary hover:text-primary underline" target="_blank" rel="noopener noreferrer">
              {children}
            </a>
          ),
          table: ({ children }) => (
            <div className="overflow-x-auto my-4">
              <table className="min-w-full divide-y divide-border border border-border">
                {children}
              </table>
            </div>
          ),
          th: ({ children }) => (
            <th className="px-4 py-2 bg-muted/40 text-left text-sm font-semibold text-muted-foreground border">
              {children}
            </th>
          ),
          td: ({ children }) => (
            <td className="px-4 py-2 text-sm text-muted-foreground border">
              {children}
            </td>
          ),
        }}
      >
        {markdownBody}
      </ReactMarkdown>
    </div>
  );
}
